import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import GenericHeader from "../../../Componentes/GenericHeader";
import FilterContainer from "../../../Componentes/Utils/FilterContainer";
import Filters from "../../../Componentes/Utils/Filters";
import MultiselectCheck from "../../../Componentes/Utils/MultiselectCheck";
import SortableTable from "../../../Componentes/Utils/SortableTable";
import { formatCPF } from "../../../Funcoes/FormatCPF";
import useApi from "../../../hooks/useApi";
import ModalDetalhe from "./ModalDetalhe";

const PAGE_SIZE = 150;

const STATUS_OPTIONS = [
    { value: "OK", label: "OK" },
    { value: "PENDENTE", label: "PENDENTE" },
    { value: "SEM_CADASTRO", label: "SEM CADASTRO" },
];

const PageContent = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    box-sizing: border-box;
    overflow: hidden;
`;

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

const Statistics = styled.div`
    width: 220px;
    flex-shrink: 0;
    height: 100%;
    background-color: #f3f3f3;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    padding: 10px 14px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const StatLine = styled.p`
    margin: 0;
    padding: 0;
    font-size: 13px;
    font-weight: 700;
    color: ${({ $status }) => {
        if ($status === "OK") return "#1b5e20";
        if ($status === "PENDENTE") return "#b71c1c";
        return "#5d4037";
    }};
`;

const StatusBadge = styled.span`
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 700;
    color: ${({ $status }) => {
        if ($status === "OK") return "#1b5e20";
        if ($status === "PENDENTE") return "#b71c1c";
        return "#5d4037";
    }};
    background: ${({ $status }) => {
        if ($status === "OK") return "#c8e6c9";
        if ($status === "PENDENTE") return "#ffcdd2";
        return "#d7ccc8";
    }};
`;

const statusLabel = (status) => {
    if (status === "SEM_CADASTRO") return "SEM CADASTRO";
    return status || "-";
};

const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

const uniqueSorted = (values) =>
    [...new Set(values.map((v) => String(v ?? "").trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, "pt-BR")
    );

const Content = () => {
    const api = useApi();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [linhas, setLinhas] = useState(PAGE_SIZE);
    const [colabSelected, setColabSelected] = useState(null);

    const [matricula, setMatricula] = useState("");
    const [nome, setNome] = useState("");
    const [cpf, setCpf] = useState("");
    const [selectedFuncoes, setSelectedFuncoes] = useState([]);
    const [selectedCentrosCusto, setSelectedCentrosCusto] = useState([]);
    const [selectedGhes, setSelectedGhes] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState([]);

    useEffect(() => {
        let cancelled = false;

        const fetchData = async () => {
            setLoading(true);
            try {
                const { data: response } = await api.get(
                    "/Painel/API/Treinamentos/TreinamentosStatus/"
                );
                if (!cancelled) {
                    setData(Array.isArray(response) ? response : []);
                }
            } catch (error) {
                console.error(error);
                if (!cancelled) {
                    setData([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            cancelled = true;
        };
    }, [api]);

    const opcoesFuncoes = useMemo(
        () =>
            uniqueSorted(data.map((row) => row.funcao)).map((opt) => ({
                value: opt,
                label: opt,
            })),
        [data]
    );

    const opcoesCentrosCusto = useMemo(
        () =>
            uniqueSorted(data.map((row) => row.centro_custo)).map((opt) => ({
                value: opt,
                label: opt,
            })),
        [data]
    );

    const opcoesGhes = useMemo(
        () =>
            uniqueSorted(data.map((row) => row.ghe)).map((opt) => ({
                value: opt,
                label: opt,
            })),
        [data]
    );

    const dataFiltered = useMemo(() => {
        const matriculaFilter = matricula.trim().toLowerCase();
        const nomeFilter = nome.trim().toLowerCase();
        const cpfFilter = onlyDigits(cpf);

        return data.filter((row) => {
            if (
                matriculaFilter &&
                !String(row.matricula || "")
                    .toLowerCase()
                    .includes(matriculaFilter)
            ) {
                return false;
            }

            if (
                nomeFilter &&
                !String(row.nome || "")
                    .toLowerCase()
                    .includes(nomeFilter)
            ) {
                return false;
            }

            if (cpfFilter && !onlyDigits(row.cpf).includes(cpfFilter)) {
                return false;
            }

            if (
                selectedFuncoes.length > 0 &&
                !selectedFuncoes.includes(String(row.funcao || "").trim())
            ) {
                return false;
            }

            if (
                selectedCentrosCusto.length > 0 &&
                !selectedCentrosCusto.includes(String(row.centro_custo || "").trim())
            ) {
                return false;
            }

            if (
                selectedGhes.length > 0 &&
                !selectedGhes.includes(String(row.ghe || "").trim())
            ) {
                return false;
            }

            if (selectedStatus.length > 0 && !selectedStatus.includes(row.status)) {
                return false;
            }

            return true;
        });
    }, [
        data,
        matricula,
        nome,
        cpf,
        selectedFuncoes,
        selectedCentrosCusto,
        selectedGhes,
        selectedStatus,
    ]);

    useEffect(() => {
        setLinhas(PAGE_SIZE);
    }, [
        matricula,
        nome,
        cpf,
        selectedFuncoes,
        selectedCentrosCusto,
        selectedGhes,
        selectedStatus,
    ]);

    const columns = useMemo(
        () => [
            {
                id: "matricula",
                header: "MATRÍCULA",
                accessor: "matricula",
                sortable: true,
                width: "110px",
            },
            {
                id: "nome",
                header: "NOME",
                accessor: "nome",
                sortable: true,
            },
            {
                id: "cpf",
                header: "CPF",
                accessor: (row) => (row.cpf ? formatCPF(String(row.cpf)) : "-"),
                sortable: true,
                width: "140px",
                sortValue: (row) => onlyDigits(row.cpf),
            },
            {
                id: "funcao",
                header: "FUNÇÃO",
                accessor: (row) => row.funcao || "-",
                sortable: true,
            },
            {
                id: "centro_custo",
                header: "CENTRO DE CUSTO",
                accessor: (row) => row.centro_custo || "-",
                sortable: true,
            },
            {
                id: "ghe",
                header: "GHE",
                accessor: (row) => row.ghe || "-",
                sortable: true,
                width: "120px",
            },
            {
                id: "obrigatorios",
                header: "OBRIGATÓRIOS",
                accessor: "obrigatorios",
                sortable: true,
                width: "120px",
                align: "center",
                sortValue: (row) => Number(row.obrigatorios ?? 0),
            },
            {
                id: "pendentes",
                header: "PENDENTES",
                accessor: "pendentes",
                sortable: true,
                width: "110px",
                align: "center",
                sortValue: (row) => Number(row.pendentes ?? 0),
            },
            {
                id: "status",
                header: "STATUS",
                accessor: (row) => (
                    <StatusBadge $status={row.status}>
                        {statusLabel(row.status)}
                    </StatusBadge>
                ),
                sortable: true,
                width: "140px",
                align: "center",
                sortValue: (row) => row.status || "",
            },
        ],
        []
    );

    const contagens = useMemo(() => {
        let ok = 0;
        let pendentes = 0;
        let semCadastro = 0;

        dataFiltered.forEach((row) => {
            if (row.status === "OK") ok += 1;
            else if (row.status === "PENDENTE") pendentes += 1;
            else if (row.status === "SEM_CADASTRO") semCadastro += 1;
        });

        return { ok, pendentes, semCadastro };
    }, [dataFiltered]);

    const loadMoreRows = () => {
        if (linhas >= dataFiltered.length) return;
        setLinhas((prev) => prev + PAGE_SIZE);
    };

    return (
        <PageContent>
            <GenericHeader responsive>
                <Filters columns={4}>
                    <FilterContainer
                        type="text"
                        placeholder="MATRÍCULA..."
                        valor={matricula}
                        changeFunc={setMatricula}
                    />
                    <FilterContainer
                        type="text"
                        placeholder="NOME..."
                        valor={nome}
                        changeFunc={setNome}
                    />
                    <FilterContainer
                        type="text"
                        placeholder="CPF..."
                        valor={cpf}
                        changeFunc={(value) => setCpf(formatCPF(String(value || "")))}
                        maxLength={14}
                    />
                    <MultiselectCheck
                        options={opcoesFuncoes}
                        selectedOptions={selectedFuncoes}
                        setSelectedOptions={setSelectedFuncoes}
                        placeholder="FUNÇÃO..."
                    />
                    <MultiselectCheck
                        options={opcoesCentrosCusto}
                        selectedOptions={selectedCentrosCusto}
                        setSelectedOptions={setSelectedCentrosCusto}
                        placeholder="CENTRO CUSTO..."
                    />
                    <MultiselectCheck
                        options={opcoesGhes}
                        selectedOptions={selectedGhes}
                        setSelectedOptions={setSelectedGhes}
                        placeholder="GHE..."
                    />
                    <MultiselectCheck
                        options={STATUS_OPTIONS}
                        selectedOptions={selectedStatus}
                        setSelectedOptions={setSelectedStatus}
                        placeholder="STATUS..."
                    />
                </Filters>
                <Statistics>
                    <StatLine $status="OK">OK: {contagens.ok}</StatLine>
                    <StatLine $status="PENDENTE">PENDENTES: {contagens.pendentes}</StatLine>
                    <StatLine $status="SEM_CADASTRO">
                        SEM CADASTRO: {contagens.semCadastro}
                    </StatLine>
                </Statistics>
            </GenericHeader>

            <TableFlexFill>
                <SortableTable
                    columns={columns}
                    data={dataFiltered}
                    isLoading={loading}
                    loadingText="Carregando status de treinamentos..."
                    emptyText="NENHUM COLABORADOR ENCONTRADO"
                    initialSort={{ key: "nome", direction: "asc" }}
                    getRowId={(row) => row.cpf || row.matricula}
                    limit={linhas}
                    onScrollReachEnd={loadMoreRows}
                    onRowClick={setColabSelected}
                    rowCursor="pointer"
                />
            </TableFlexFill>

            {colabSelected && (
                <ModalDetalhe
                    colaborador={colabSelected}
                    onClose={() => setColabSelected(null)}
                />
            )}
        </PageContent>
    );
};

export default Content;
